import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../config/api';
import './BrokerConnection.css';

interface BrokerConnectionProps {
  onConnect?: (connectionKey: string) => void;
}

const BrokerConnection: React.FC<BrokerConnectionProps> = ({ onConnect }) => {
  const [apiToken, setApiToken] = useState('');
  const [accountId, setAccountId] = useState('');
  const [sandbox, setSandbox] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionKey, setConnectionKey] = useState('');
  const [accountInfo, setAccountInfo] = useState<any>(null);
  const [error, setError] = useState('');

  // Load saved connection on component mount
  useEffect(() => {
    const savedConnections = localStorage.getItem('broker_connections');
    if (savedConnections) {
      try {
        const connections = JSON.parse(savedConnections);
        // Find TBANK connection
        const tbankConnection = connections.find((c: any) => c.broker === 'TBANK');

        if (tbankConnection) {
          setConnectionKey(tbankConnection.connection_key);
          setApiToken(tbankConnection.api_token);
          setAccountId(tbankConnection.account_id || '');
          setSandbox(tbankConnection.sandbox);
          setIsConnected(true);

          // Restore connection
          fetchAccountInfo(tbankConnection.connection_key);

          if (onConnect) {
            onConnect(tbankConnection.connection_key);
          }
        }
      } catch (err) {
        console.error('Failed to restore connection:', err);
      }
    }
  }, [onConnect]);

  const handleConnect = async () => {
    setIsConnecting(true);
    setError('');

    try {
      // Create AbortController with 40 second timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 40000);

      const response = await fetch(getApiUrl('/brokers/connect'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          broker: 'TBANK',
          credentials: {
            api_token: apiToken,
            account_id: accountId || undefined,
            sandbox: sandbox,
          },
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to connect');
      }

      setConnectionKey(data.connection_key);
      setIsConnected(true);

      // Save connection to localStorage (supports multiple brokers)
      const savedConnections = localStorage.getItem('broker_connections');
      const connections = savedConnections ? JSON.parse(savedConnections) : [];

      const newConnection = {
        broker: 'TBANK',
        connection_key: data.connection_key,
        api_token: apiToken,
        account_id: accountId,
        sandbox: sandbox,
      };

      // Remove old TBANK connection if exists
      const filteredConnections = connections.filter((c: any) => c.broker !== 'TBANK');
      localStorage.setItem('broker_connections', JSON.stringify([...filteredConnections, newConnection]));

      // Fetch account info
      await fetchAccountInfo(data.connection_key);

      if (onConnect) {
        onConnect(data.connection_key);
      }
    } catch (err: any) {
      setError(err.message || 'Connection failed');
    } finally {
      setIsConnecting(false);
    }
  };

  const fetchAccountInfo = async (key: string) => {
    try {
      const response = await fetch(getApiUrl(`/brokers/${key}/account`), { credentials: 'include' });
      const data = await response.json();
      setAccountInfo(data);
    } catch (err) {
      console.error('Failed to fetch account info:', err);
    }
  };

  const handleDisconnect = () => {
    setIsConnected(false);
    setConnectionKey('');
    setAccountInfo(null);
    setApiToken('');
    setAccountId('');

    // Remove TBANK connection from array
    const savedConnections = localStorage.getItem('broker_connections');
    if (savedConnections) {
      const connections = JSON.parse(savedConnections);
      const filteredConnections = connections.filter((c: any) => c.broker !== 'TBANK');
      localStorage.setItem('broker_connections', JSON.stringify(filteredConnections));
    }
  };

  return (
    <div className="broker-connection">
      <div className="card">
        <h2>Подключение ТБАНК</h2>

        {!isConnected ? (
          <div className="connection-form">
            <div className="form-group">
              <label>API Token</label>
              <input
                type="password"
                value={apiToken}
                onChange={(e) => setApiToken(e.target.value)}
                placeholder="Введите токен от Тинькофф Инвестиций"
                disabled={isConnecting}
              />
              <small>
                Получите токен в{' '}
                <a
                  href="https://www.tbank.ru/invest/settings/api/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  настройках Тинькофф Инвестиций
                </a>
              </small>
            </div>

            <div className="form-group">
              <label>Account ID (необязательно)</label>
              <input
                type="text"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                placeholder="Если не указан, будет использован основной счет"
                disabled={isConnecting}
              />
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={sandbox}
                  onChange={(e) => setSandbox(e.target.checked)}
                  disabled={isConnecting}
                />
                Песочница (тестовый режим)
              </label>
              <small>
                В песочнице вы можете тестировать торговлю без реальных денег
              </small>
            </div>

            {error && <div className="error-message">{error}</div>}

            {isConnecting && (
              <div style={{ color: '#9ca3af', fontSize: '13px', marginTop: '8px' }}>
                Подключение к брокеру может занять до 30 секунд...
              </div>
            )}

            <button
              className="primary"
              onClick={handleConnect}
              disabled={isConnecting || !apiToken}
            >
              {isConnecting ? 'Подключение...' : 'Подключить'}
            </button>
          </div>
        ) : (
          <div className="connection-info">
            <div className="status-badge connected">Подключено</div>

            {accountInfo && (
              <div className="account-details">
                <h3>Информация о счете</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">Брокер</span>
                    <span className="value">{accountInfo.broker}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Баланс</span>
                    <span className="value">
                      {accountInfo.balance?.toLocaleString('ru-RU')} {accountInfo.currency}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="label">Режим</span>
                    <span className="value">
                      {accountInfo.sandbox ? 'Песочница' : 'Боевой'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="label">Открытые позиции</span>
                    <span className="value">{accountInfo.positions?.length || 0}</span>
                  </div>
                </div>
              </div>
            )}

            <button className="secondary" onClick={handleDisconnect}>
              Отключить
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrokerConnection;
